import { decSum, formatDec, isDecimalText, utcToLocal } from '@felix/domain';
import { Alert, Button, Modal, Select, Textarea, TextInput } from '@mantine/core';
import {
  IconAdjustments,
  IconClipboardList,
  IconDeviceFloppy,
  IconFileText,
  IconInfoCircle,
  IconPlus,
  IconRuler2,
  IconScale,
  IconSend,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { DateTime } from './BunkeringPanel.js';
import classes from './Ledger.module.css';
import {
  DecisionModal,
  FileField,
  issuesOf,
  mt,
  Notices,
  newKey,
  Section,
  StatusBadge,
  useLedgerError,
} from './ledger-ui.js';
import { PeriodPanel } from './PeriodPanel.js';
import cx from './Reconciliation.module.css';
import {
  adjustmentLinesOf,
  needsBasis,
  previewComparison,
  previewLine,
  type SoundingLineValues,
  type SoundingValues,
  soundingInputOf,
} from './reconciliation-form.js';
import type { FuelSource } from './source.js';
import type {
  FuelComparison,
  FuelComparisonRow,
  FuelContextData,
  FuelReconciliationData,
  FuelRecordDetail,
  FuelTanksData,
  FuelThresholds,
} from './types.js';

const SOURCES = ['ullage_report', 'sounding_report', 'bunker_survey', 'voyage_report', 'other'];
const METHODS = ['sounding', 'ullage', 'sensor', 'manual'] as const;
const REASONS = [
  'measurement_tolerance',
  'measurement_error',
  'calibration',
  'density',
  'temperature',
  'unrecorded_consumption',
  'sludge_water',
  'bunker_difference',
  'other',
] as const;
const DESCRIPTION_MAX = 1000;

/**
 * Bağlantı sorgusu: gemi (`?vessel=`; eski `?vesselId=` de okunur) ve panodaki düzeltme satırı
 * (`?adjustment=`; ADR 0156 "Yollar").
 */
export function robLinkOf(search: string): {
  vesselId: string | null;
  adjustmentId: string | null;
} {
  const params = new URLSearchParams(search);
  return {
    vesselId: params.get('vessel') ?? params.get('vesselId'),
    adjustmentId: params.get('adjustment'),
  };
}

export interface RobReconciliationPanelProps {
  source: FuelSource;
  vesselId: string | null;
  /** Panodan açılan düzeltme (ADR 0156): sayımı seçilir ve düzeltme kaydı açılır. */
  adjustmentId?: string | null;
  onVessel?: (data: {
    vessel: { id: string; name: string };
    vessels?: { id: string; name: string }[];
  }) => void;
}

interface AdjustmentValues {
  reasonCode: string | null;
  description: string;
  lines: { tankId: string; fuelTypeId: string | null; massMt: string }[];
}

const EMPTY_ADJUSTMENT: AdjustmentValues = { reasonCode: null, description: '', lines: [] };

function signed(value: string): string {
  const shown = mt(value);
  return shown.startsWith('-') || shown === mt('0') ? shown : `+${shown}`;
}

/** Ölçüm değeri gösterimi: sabit ondalık (tasarımdaki gibi), değer yoksa tire. */
function fixed(value: string | null, digits: number): string {
  return value === null ? '—' : mt(value, digits);
}

/**
 * ROB Mutabakatı (ortağın 06-rob-mutabakati.jpeg tasarımı; ortak belgesi §10; ADR 0117 §4, Ek Y4):
 * ölçüm başlığı, tank ölçümleri (yöntem, okuma, sıcaklık, hacim, yoğunluk, ölçülen MT; VCF 54B),
 * yakıt bazında ve genel karşılaştırma, mutabakat ve düzeltme talebi. Ölçüm stok bakiyesini
 * değiştirmez; fark yalnız gemi onayı ve ofis adımından sonra düzeltme hareketiyle deftere girer.
 * Altta ROB ay kapanışı. Ofiste salt okunur; ofis düzeltmenin ofis adımına karar verir ve eşikleri
 * yazar.
 */
export function RobReconciliationPanel({
  source,
  vesselId,
  adjustmentId = null,
  onVessel,
}: RobReconciliationPanelProps) {
  const { t } = useTranslation();
  // Panodan gelen düzeltme yalnız ilk okumada açılır
  const linked = useRef(adjustmentId);
  const errorText = useLedgerError();
  const [data, setData] = useState<FuelReconciliationData | null>(null);
  const [context, setContext] = useState<FuelContextData | null>(null);
  const [tanksData, setTanksData] = useState<FuelTanksData | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [soundingId, setSoundingId] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'new'>('view');
  const [values, setValues] = useState<SoundingValues | null>(null);
  const [key, setKey] = useState(newKey);
  const [adjustmentKey, setAdjustmentKey] = useState(newKey);
  const [detail, setDetail] = useState<FuelRecordDetail | null>(null);
  const [adjustment, setAdjustment] = useState<AdjustmentValues>(EMPTY_ADJUSTMENT);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [done, setDone] = useState<string | null>(null);
  const [modal, setModal] = useState<'approve' | 'return' | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(
    async (select?: string | null) => {
      if (!source.reconciliation) return;
      const wanted = linked.current;
      linked.current = null;
      try {
        // Düzeltmenin sayımı (kayıt girdisinin `soundingId`'si); okunamazsa son sayım açılır
        let sounding = select ?? null;
        if (wanted && sounding === null) {
          const record = await source.record(vesselId, 'adjustment', wanted).catch(() => null);
          const id = record?.input.soundingId;
          sounding = typeof id === 'string' ? id : null;
        }
        const [view, ctx, tanks] = await Promise.all([
          source.reconciliation(vesselId, sounding),
          source.context(vesselId),
          source.tanks(vesselId),
        ]);
        setData(view);
        setContext(ctx);
        setTanksData(tanks);
        setSoundingId(view.sounding?.id ?? null);
        setFailed(null);
        onVessel?.(view);
        const live =
          view.adjustments.find((a) => a.id === wanted) ??
          view.adjustments.find((a) => a.status !== 'reversed' && a.status !== 'corrected');
        const record = live ? await source.record(vesselId, 'adjustment', live.id) : null;
        setDetail(record);
        setAdjustment(
          record
            ? {
                reasonCode: (record.input.reasonCode as string | null) ?? null,
                description: (record.input.description as string | null) ?? '',
                lines: ((record.input.lines as AdjustmentValues['lines']) ?? []).map((l) => ({
                  tankId: l.tankId,
                  fuelTypeId: l.fuelTypeId,
                  massMt: l.massMt ?? '0',
                })),
              }
            : {
                ...EMPTY_ADJUSTMENT,
                lines: view.comparison ? adjustmentLinesOf(view.comparison, false) : [],
              },
        );
        setAdjustmentKey(newKey());
        setFile(null);
        setMode(view.sounding ? 'view' : 'new');
      } catch (failure) {
        setFailed(errorCode(failure));
      }
    },
    [source, vesselId, onVessel],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const activeTanks = useMemo(
    () => (tanksData?.tanks ?? []).filter((tank) => tank.active),
    [tanksData],
  );
  const calibratedTankIds = useMemo(
    () => activeTanks.filter((tank) => tank.calibration !== null).map((tank) => tank.id),
    [activeTanks],
  );

  const blankValues = useCallback(
    (): SoundingValues => ({
      day: null,
      time: null,
      sourceKind: 'ullage_report',
      sourceReference: '',
      measuredById: context?.me ?? null,
      basis: '',
      lines: activeTanks.map((tank) => ({
        tankId: tank.id,
        method: (METHODS as readonly string[]).includes(tank.measurementMethod)
          ? (tank.measurementMethod as SoundingLineValues['method'])
          : 'sounding',
        readingCm: '',
        tempC: '',
        observedVolumeM3: '',
        density15: context?.tanks.find((x) => x.id === tank.id)?.density15 ?? '',
        massMt: '',
      })),
    }),
    [activeTanks, context],
  );

  useEffect(() => {
    if (mode === 'new') setValues(blankValues());
  }, [mode, blankValues]);

  if (!source.reconciliation) return null;
  if (failed) {
    return <LoadError code={failed} onRetry={() => void load()} title={t('fuel.rob.title')} />;
  }
  if (!data || !context) {
    return <TableSkeleton columns={6} rows={8} label={t('fuel.rob.title')} />;
  }

  const ship = source.soundingEditor !== undefined;
  const fuelCode = (id: string | null) => context.fuelTypes.find((f) => f.id === id)?.code ?? '—';
  const tankName = (id: string) => {
    const tank = tanksData?.tanks.find((x) => x.id === id);
    return tank ? tank.name : '—';
  };
  const issue = (path: string) => {
    const code = issues[path];
    return code ? t(`fuel.fieldErrors.${code}`, { defaultValue: code }) : null;
  };
  const crewOptions = context.crew.map((p) => ({
    value: p.id,
    label: p.role ? `${p.name} (${p.role})` : p.name,
  }));
  const sounding = mode === 'view' ? data.sounding : null;
  const form = mode === 'new' ? values : null;
  const comparison: FuelComparison | null =
    mode === 'view'
      ? data.comparison
      : form
        ? (previewComparison(form, {
            tanks: context.tanks.map((x) => ({
              id: x.id,
              fuelTypeId: x.fuelTypeId,
              robMt: x.robMt,
            })),
            thresholds: data.thresholds,
          }) as FuelComparison | null)
        : null;

  const run = async (
    action: string,
    work: () => Promise<string | null | undefined>,
    message: string,
  ) => {
    setBusy(action);
    setError(null);
    setIssues({});
    setDone(null);
    try {
      const select = await work();
      await load(select ?? soundingId);
      setDone(message);
    } catch (failure) {
      setError(errorText(failure));
      setIssues(issuesOf(failure));
    } finally {
      setBusy(null);
    }
  };

  const saveSounding = () =>
    run(
      'save',
      async () => {
        if (!source.soundingEditor || !form) return null;
        const saved = await source.soundingEditor.record({
          idempotencyKey: key,
          input: soundingInputOf(form, { calibratedTankIds }),
        });
        setKey(newKey());
        return saved.id;
      },
      t('fuel.rob.saved'),
    );

  const adjustmentInput = () => ({
    soundingId,
    reasonCode: adjustment.reasonCode,
    description: adjustment.description.trim() === '' ? null : adjustment.description.trim(),
    lines: adjustment.lines.map((l) => ({
      tankId: l.tankId,
      fuelTypeId: l.fuelTypeId,
      massMt: isDecimalText(l.massMt.replace(',', '.')) ? l.massMt.replace(',', '.') : null,
    })),
  });

  const persistAdjustment = async (): Promise<string> => {
    const editor = source.ledgerEditor;
    if (!editor) throw Object.assign(new Error('FORBIDDEN'), { code: 'FORBIDDEN' });
    let id = detail?.id ?? null;
    if (id === null) {
      id = (
        await editor.save('adjustment', { idempotencyKey: adjustmentKey, input: adjustmentInput() })
      ).id;
    } else if (detail && (detail.status === 'draft' || detail.status === 'returned')) {
      await editor.save('adjustment', {
        id,
        rowVersion: detail.rowVersion,
        input: adjustmentInput(),
      });
    }
    if (file) {
      const stored = await editor.upload(file);
      await editor.attach('adjustment', id, {
        sha256: stored.sha256,
        fileName: file.name,
        document: 'measurement',
      });
    }
    return id;
  };

  const requestAdjustment = () =>
    run(
      'request',
      async () => {
        await persistAdjustment();
        return soundingId;
      },
      t('fuel.rob.requested'),
    );

  const submitAdjustment = () =>
    run(
      'submit',
      async () => {
        const id = await persistAdjustment();
        const fresh = await source.record(vesselId, 'adjustment', id);
        await source.ledgerEditor?.submit('adjustment', id, fresh.approval.contentHash);
        return soundingId;
      },
      t('fuel.records.submitted'),
    );

  const decide = async (
    mode2: 'approve' | 'return' | 'reverse',
    input: { reason: string; password?: string },
  ) => {
    if (!detail?.approval.pending || mode2 === 'reverse') return;
    const pending = detail.approval.pending;
    const body = {
      decision: mode2 === 'approve' ? ('approved' as const) : ('returned' as const),
      reason: mode2 === 'return' ? input.reason : null,
      contentHash: detail.approval.contentHash,
      expected: { submissionId: pending.submissionId, step: pending.step },
      ...(input.password ? { password: input.password } : {}),
    };
    if (pending.office) await source.officeEditor?.decideAdjustment(vesselId, detail.id, body);
    else await source.ledgerEditor?.decide('adjustment', detail.id, body);
    await load(soundingId);
    setDone(mode2 === 'approve' ? t('fuel.records.approved') : t('fuel.records.returned'));
  };

  const adjustmentEditable =
    ship &&
    data.can.adjust &&
    sounding !== null &&
    (detail === null || detail.status === 'draft' || detail.status === 'returned');
  const pending = detail?.approval.pending ?? null;
  const canDecide = pending
    ? pending.office
      ? !ship &&
        (data.can.officeDecideIds
          ? data.can.officeDecideIds.includes(detail?.id ?? '')
          : data.can.officeDecide)
      : ship && (context.can.approve || context.can.return)
    : false;
  const adjustmentTotal = formatDec(
    decSum(
      adjustment.lines.map((l) => {
        const v = l.massMt.replace(',', '.');
        return isDecimalText(v) ? v : '0';
      }),
    ),
  );
  // Gösterimde sıfır olan fark vurgulanmaz (yuvarlama yalnız gösterimde; ADR 0117 §11)
  const status = (row: FuelComparisonRow) =>
    row.status === 'exceeds' ? cx.exceeds : mt(row.diffMt) !== mt('0') ? cx.warn : undefined;
  const at = (iso: string, offset: number) => {
    const local = utcToLocal(iso, offset);
    return `${local.slice(8, 10)}.${local.slice(5, 7)}.${local.slice(0, 4)} ${local.slice(11, 16)}`;
  };
  const soundingLabel = (s: { soundingNo: string; measuredAt: string; tzOffsetMin: number }) =>
    t('fuel.rob.soundingOption', { no: s.soundingNo, at: at(s.measuredAt, s.tzOffsetMin) });
  const lineBasisRequired = form ? form.lines.some((l) => needsBasis(l, calibratedTankIds)) : false;
  const methodsWithoutThreshold = (comparison?.tanks ?? []).some(
    (row) => row.status === 'undefined',
  );

  const updateLine = (index: number, patch: Partial<SoundingLineValues>) =>
    setValues((prev) =>
      prev
        ? { ...prev, lines: prev.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)) }
        : prev,
    );

  return (
    <div className={classes.alerts}>
      <section className={classes.card} aria-label={t('fuel.rob.title')}>
        <div className={classes.head}>
          <h2 className={classes.title}>{t('fuel.rob.title')}</h2>
          <div className={classes.picker}>
            <Select
              className={classes.pickerSelect}
              aria-label={t('fuel.rob.sounding')}
              placeholder={
                data.soundings.length === 0 ? t('fuel.rob.noSounding') : t('fuel.rob.sounding')
              }
              data={data.soundings.map((s) => ({ value: s.id, label: soundingLabel(s) }))}
              value={mode === 'view' ? soundingId : null}
              allowDeselect={false}
              disabled={data.soundings.length === 0}
              onChange={(value) => {
                if (value) void load(value);
              }}
            />
            {ship && data.can.measure ? (
              <Button
                variant="default"
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setError(null);
                  setDone(null);
                  setMode('new');
                }}
              >
                {t('fuel.rob.newSounding')}
              </Button>
            ) : null}
            {!ship && data.can.settings && source.officeEditor ? (
              <Button
                variant="default"
                leftSection={<IconAdjustments size={16} />}
                onClick={() => setSettingsOpen(true)}
              >
                {t('fuel.rob.thresholds')}
              </Button>
            ) : null}
          </div>
        </div>
        <div className={classes.body}>
          {ship ? null : (
            <Alert className={classes.banner} color="blue" role="note">
              {t('fuel.rob.officeReadOnly')}
            </Alert>
          )}
          <Notices error={error} done={done} />
          {mode === 'view' && !sounding ? (
            <div className={classes.empty}>
              <IconScale size={36} className={classes.emptyIcon} aria-hidden />
              <strong>{t('fuel.rob.noSounding')}</strong>
              {t('fuel.rob.noSoundingBody')}
            </div>
          ) : null}

          {sounding || form ? (
            <>
              <Section icon={<IconClipboardList size={22} />} title={t('fuel.rob.info')}>
                <div className={cx.infoGrid}>
                  {form ? (
                    <DateTime
                      label={t('fuel.rob.measuredAt')}
                      day={form.day}
                      time={form.time}
                      readOnly={false}
                      onDay={(v) => setValues((p) => (p ? { ...p, day: v } : p))}
                      onTime={(v) => setValues((p) => (p ? { ...p, time: v } : p))}
                      error={issue('measuredLocal')}
                    />
                  ) : (
                    <TextInput
                      label={t('fuel.rob.measuredAt')}
                      withAsterisk
                      readOnly
                      value={sounding ? at(sounding.measuredAt, sounding.tzOffsetMin) : ''}
                    />
                  )}
                  <Select
                    label={t('fuel.rob.sourceKind')}
                    withAsterisk
                    readOnly={!form}
                    data={SOURCES.map((s) => ({ value: s, label: t(`fuel.rob.sources.${s}`) }))}
                    value={form ? form.sourceKind : (sounding?.sourceKind ?? null)}
                    onChange={(v) => setValues((p) => (p ? { ...p, sourceKind: v } : p))}
                    error={issue('sourceKind')}
                  />
                  <Select
                    label={t('fuel.rob.measuredBy')}
                    withAsterisk
                    readOnly={!form}
                    searchable
                    data={
                      form
                        ? crewOptions
                        : [
                            {
                              value: sounding?.measuredById ?? '-',
                              label: sounding?.measuredByName ?? '—',
                            },
                          ]
                    }
                    value={form ? form.measuredById : (sounding?.measuredById ?? '-')}
                    onChange={(v) => setValues((p) => (p ? { ...p, measuredById: v } : p))}
                    error={issue('measuredById')}
                  />
                  <TextInput
                    label={t('fuel.rob.sourceReference')}
                    readOnly={!form}
                    value={form ? form.sourceReference : (sounding?.sourceReference ?? '')}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setValues((p) => (p ? { ...p, sourceReference: v } : p));
                    }}
                  />
                  {form && lineBasisRequired ? (
                    <TextInput
                      className={cx.wide}
                      label={t('fuel.rob.basis')}
                      withAsterisk
                      description={t('fuel.rob.basisHint')}
                      value={form.basis}
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        setValues((p) => (p ? { ...p, basis: v } : p));
                      }}
                      error={
                        Object.keys(issues).some((k) => k.endsWith('.basis'))
                          ? t('fuel.fieldErrors.REQUIRED')
                          : null
                      }
                    />
                  ) : null}
                </div>
              </Section>

              <Section icon={<IconRuler2 size={22} />} title={t('fuel.rob.tanks')}>
                <div
                  className={classes.tableWrap}
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                  tabIndex={0}
                >
                  <table className={`${classes.table} ${cx.tanks}`}>
                    <thead>
                      <tr>
                        <th>{t('fuel.rob.columns.tank')}</th>
                        <th>{t('fuel.rob.columns.method')}</th>
                        <th className={classes.num}>{t('fuel.rob.columns.reading')}</th>
                        <th className={classes.num}>{t('fuel.rob.columns.temp')}</th>
                        <th className={classes.num}>{t('fuel.rob.columns.volume')}</th>
                        <th className={classes.num}>{t('fuel.rob.columns.density')}</th>
                        <th className={classes.num}>{t('fuel.rob.columns.measured')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form ? (
                        form.lines.length === 0 ? (
                          <tr>
                            <td colSpan={7}>{t('fuel.rob.noTanks')}</td>
                          </tr>
                        ) : (
                          form.lines.map((line, index) => {
                            const name = tankName(line.tankId);
                            const preview = previewLine(line);
                            const manual = line.method === 'manual';
                            const cell = (
                              field: 'readingCm' | 'tempC' | 'observedVolumeM3' | 'density15',
                              label: string,
                            ) => (
                              <td>
                                <TextInput
                                  aria-label={`${label} — ${name}`}
                                  inputMode="decimal"
                                  className={cx.numInput}
                                  disabled={manual}
                                  value={manual ? '' : line[field]}
                                  onChange={(e) =>
                                    updateLine(index, {
                                      [field]: e.currentTarget.value.replace(',', '.'),
                                    })
                                  }
                                  error={issue(`lines.${index}.${field}`) ? ' ' : null}
                                />
                              </td>
                            );
                            return (
                              <tr key={line.tankId}>
                                <td className={cx.tankCell}>{name}</td>
                                <td>
                                  <Select
                                    aria-label={`${t('fuel.rob.columns.method')} — ${name}`}
                                    data={METHODS.map((m) => ({
                                      value: m,
                                      label: t(`fuel.rob.methods.${m}`),
                                    }))}
                                    value={line.method}
                                    allowDeselect={false}
                                    onChange={(v) => {
                                      if (v)
                                        updateLine(index, {
                                          method: v as SoundingLineValues['method'],
                                        });
                                    }}
                                  />
                                </td>
                                {cell('readingCm', t('fuel.rob.columns.reading'))}
                                {cell('tempC', t('fuel.rob.columns.temp'))}
                                {cell('observedVolumeM3', t('fuel.rob.columns.volume'))}
                                {cell('density15', t('fuel.rob.columns.density'))}
                                <td>
                                  <TextInput
                                    aria-label={`${t('fuel.rob.columns.measured')} — ${name}`}
                                    inputMode="decimal"
                                    className={`${cx.numInput} ${manual ? '' : classes.computed}`}
                                    readOnly={!manual}
                                    title={
                                      preview?.vcf
                                        ? t('fuel.rob.conversionNoTable', {
                                            vcf: preview.vcf,
                                            v15: preview.correctedVolumeM3,
                                          })
                                        : undefined
                                    }
                                    value={manual ? line.massMt : preview ? mt(preview.massMt) : ''}
                                    onChange={(e) =>
                                      updateLine(index, {
                                        massMt: e.currentTarget.value.replace(',', '.'),
                                      })
                                    }
                                    error={issue(`lines.${index}.massMt`) ? ' ' : null}
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )
                      ) : (
                        (sounding?.lines ?? []).map((line) => {
                          const version = tanksData?.tanks.find((x) => x.id === line.tankId)
                            ?.calibration?.version;
                          return (
                            <tr key={line.tankId}>
                              <td className={cx.tankCell}>{tankName(line.tankId)}</td>
                              <td>{t(`fuel.rob.methods.${line.method}`)}</td>
                              <td className={classes.num}>{fixed(line.readingCm, 1)}</td>
                              <td className={classes.num}>{fixed(line.tempC, 1)}</td>
                              <td className={classes.num}>{fixed(line.observedVolumeM3, 2)}</td>
                              <td className={classes.num}>{fixed(line.density15, 3)}</td>
                              <td
                                className={classes.num}
                                title={
                                  line.vcf
                                    ? line.conversion === 'calibration_table' && version
                                      ? t('fuel.rob.conversion', {
                                          vcf: line.vcf,
                                          v15: line.correctedVolumeM3,
                                          version,
                                        })
                                      : t('fuel.rob.conversionNoTable', {
                                          vcf: line.vcf,
                                          v15: line.correctedVolumeM3,
                                        })
                                    : (line.basis ?? undefined)
                                }
                              >
                                <strong>{mt(line.massMt)}</strong>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </Section>

              <div className={cx.compare}>
                <section className={cx.compareCard} aria-label={t('fuel.rob.byFuel')}>
                  <h3 className={cx.compareTitle}>{t('fuel.rob.byFuel')}</h3>
                  <div
                    className={classes.tableWrap}
                    // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                    tabIndex={0}
                  >
                    <table className={`${classes.table} ${cx.compareTable}`}>
                      <thead>
                        <tr>
                          <th>{t('fuel.rob.compareColumns.fuel')}</th>
                          <th className={classes.num}>{t('fuel.rob.compareColumns.calculated')}</th>
                          <th className={classes.num}>{t('fuel.rob.compareColumns.measured')}</th>
                          <th className={classes.num}>{t('fuel.rob.compareColumns.diff')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(comparison?.fuels ?? []).map((row) => (
                          <tr key={row.fuelTypeId}>
                            <td>{fuelCode(row.fuelTypeId)}</td>
                            <td className={classes.num}>{mt(row.calculatedMt)}</td>
                            <td className={classes.num}>{mt(row.measuredMt)}</td>
                            <td
                              className={`${classes.num} ${cx.diff} ${status(row) ?? ''}`}
                              title={t(`fuel.rob.status.${row.status}`)}
                            >
                              {mt(row.diffMt)}
                            </td>
                          </tr>
                        ))}
                        {(comparison?.fuels ?? []).length === 0 ? (
                          <tr>
                            <td colSpan={4}>—</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </section>
                <section className={cx.compareCard} aria-label={t('fuel.rob.total')}>
                  <h3 className={cx.compareTitle}>{t('fuel.rob.total')}</h3>
                  <dl className={cx.totals}>
                    <dt>{t('fuel.rob.calculated')}</dt>
                    <dd>{comparison ? `${mt(comparison.total.calculatedMt)} MT` : '—'}</dd>
                    <dt>{t('fuel.rob.measured')}</dt>
                    <dd>{comparison ? `${mt(comparison.total.measuredMt)} MT` : '—'}</dd>
                    <dt>{t('fuel.rob.difference')}</dt>
                    <dd
                      className={`${cx.diff} ${comparison ? (status(comparison.total) ?? '') : ''}`}
                    >
                      {comparison ? `${mt(comparison.total.diffMt)} MT` : '—'}
                    </dd>
                  </dl>
                </section>
              </div>
              {form ? <p className={classes.hint}>{t('fuel.rob.previewNote')}</p> : null}
              {comparison && methodsWithoutThreshold ? (
                <Alert color="gray" role="note" className={classes.banner}>
                  {t('fuel.rob.thresholdUndefined')}
                </Alert>
              ) : null}
              {comparison?.total.status === 'exceeds' ? (
                <Alert color="orange" role="status" className={classes.banner}>
                  {t('fuel.rob.thresholdExceeded')}
                </Alert>
              ) : null}
              {comparison && comparison.unmeasuredTankIds.length > 0 ? (
                <Alert color="yellow" role="status" className={classes.banner}>
                  {t('fuel.rob.unmeasured', {
                    tanks: comparison.unmeasuredTankIds.map(tankName).join(', '),
                  })}
                </Alert>
              ) : null}

              {sounding ? (
                <Section
                  icon={<IconScale size={22} />}
                  title={t('fuel.rob.adjustment')}
                  action={
                    detail ? (
                      <span className={classes.picker}>
                        <span className={classes.hint}>
                          {t('fuel.rob.adjustmentRecord', { no: detail.recordNo })}
                        </span>
                        <StatusBadge status={detail.status} />
                      </span>
                    ) : undefined
                  }
                >
                  {detail?.status === 'returned' && detail.approval.returned ? (
                    <Alert color="orange" role="alert" className={classes.banner}>
                      {t('fuel.records.returnedBanner', {
                        by:
                          context.crew.find((c) => c.id === detail.approval.returned?.by)?.name ??
                          '—',
                        at: at(detail.approval.returned.at, detail.tzOffsetMin),
                        reason: detail.approval.returned.reason ?? '',
                      })}
                    </Alert>
                  ) : null}
                  {pending?.office ? (
                    <Alert color="blue" role="status" className={classes.banner}>
                      {t('fuel.rob.officeStep')}
                    </Alert>
                  ) : null}
                  {detail?.status === 'approved' ? (
                    <Alert color="green" role="status" className={classes.banner}>
                      {detail.entries.length > 0
                        ? t('fuel.rob.posted', {
                            mt: mt(formatDec(decSum(detail.entries.map((e) => e.massMt)))),
                          })
                        : t('fuel.rob.appliedOnBoard')}
                    </Alert>
                  ) : null}
                  <div className={cx.adjustGrid}>
                    <Select
                      label={t('fuel.rob.reason')}
                      withAsterisk
                      readOnly={!adjustmentEditable}
                      data={REASONS.map((r) => ({ value: r, label: t(`fuel.rob.reasons.${r}`) }))}
                      value={adjustment.reasonCode}
                      onChange={(v) => setAdjustment((p) => ({ ...p, reasonCode: v }))}
                      error={issue('reasonCode')}
                    />
                    <div>
                      <Textarea
                        label={t('fuel.rob.description')}
                        autosize
                        minRows={2}
                        maxLength={DESCRIPTION_MAX}
                        readOnly={!adjustmentEditable}
                        value={adjustment.description}
                        onChange={(e) => {
                          const v = e.currentTarget.value;
                          setAdjustment((p) => ({ ...p, description: v }));
                        }}
                        error={issue('description')}
                      />
                    </div>
                    <TextInput
                      label={t('fuel.rob.adjustmentMt')}
                      readOnly
                      className={classes.computed}
                      value={mt(adjustmentTotal)}
                    />
                    <FileField
                      label={t('fuel.rob.document')}
                      file={detail?.documents.find((d) => d.document === 'measurement') ?? null}
                      pending={file}
                      editable={
                        adjustmentEditable &&
                        (file !== null ||
                          !detail?.documents.some((d) => d.document === 'measurement'))
                      }
                      onPick={setFile}
                      onRemove={() => setFile(null)}
                    />
                  </div>
                  {adjustment.lines.length > 0 ? (
                    <div className={cx.byTank}>
                      <div className={cx.byTankHead}>
                        <span className={classes.fileLabel}>{t('fuel.rob.byTank')}</span>
                        {adjustmentEditable && data.comparison ? (
                          <Button
                            size="xs"
                            variant="subtle"
                            onClick={() =>
                              setAdjustment((p) => ({
                                ...p,
                                lines: adjustmentLinesOf(data.comparison as FuelComparison, true),
                              }))
                            }
                          >
                            {t('fuel.rob.useDifferences')}
                          </Button>
                        ) : null}
                      </div>
                      <div
                        className={classes.tableWrap}
                        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                        tabIndex={0}
                      >
                        <table className={`${classes.table} ${classes.compact}`}>
                          <thead>
                            <tr>
                              <th>{t('fuel.rob.columns.tank')}</th>
                              <th>{t('fuel.rob.compareColumns.fuel')}</th>
                              <th className={classes.num}>{t('fuel.rob.tankDiff')}</th>
                              <th className={classes.num}>{t('fuel.rob.tankAdjustment')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adjustment.lines.map((line, index) => {
                              const diff =
                                data.comparison?.tanks.find((r) => r.tankId === line.tankId)
                                  ?.diffMt ?? null;
                              return (
                                <tr key={line.tankId}>
                                  <td>{tankName(line.tankId)}</td>
                                  <td>{fuelCode(line.fuelTypeId)}</td>
                                  <td className={classes.num}>{diff ? signed(diff) : '—'}</td>
                                  <td className={classes.num}>
                                    {adjustmentEditable ? (
                                      <TextInput
                                        aria-label={`${t('fuel.rob.tankAdjustment')} — ${tankName(line.tankId)}`}
                                        inputMode="decimal"
                                        className={cx.numInput}
                                        readOnly={!adjustmentEditable}
                                        value={line.massMt}
                                        onChange={(e) => {
                                          const v = e.currentTarget.value.replace(',', '.');
                                          setAdjustment((p) => ({
                                            ...p,
                                            lines: p.lines.map((l, i) =>
                                              i === index ? { ...l, massMt: v } : l,
                                            ),
                                          }));
                                        }}
                                        error={issue(`lines.${index}.massMt`) ? ' ' : null}
                                      />
                                    ) : (
                                      signed(line.massMt === '' ? '0' : line.massMt)
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </Section>
              ) : null}
            </>
          ) : null}
        </div>

        {sounding || form ? (
          <div className={`${classes.footer} ${cx.footer}`}>
            <div className={`${classes.note} ${cx.note}`} role="note">
              <IconInfoCircle size={20} style={{ color: 'var(--felix-info-fg)' }} aria-hidden />
              <span>
                <strong className={classes.noteTitle}>{t('fuel.rob.note')}</strong>{' '}
                {t('fuel.rob.noteBody')}
              </span>
            </div>
            {form && ship ? (
              <>
                {data.sounding ? (
                  <Button
                    variant="default"
                    size="md"
                    onClick={() => setMode('view')}
                    disabled={busy !== null}
                  >
                    {t('fuel.rob.cancel')}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  size="md"
                  leftSection={<IconDeviceFloppy size={18} />}
                  loading={busy === 'save'}
                  disabled={busy !== null && busy !== 'save'}
                  onClick={() => void saveSounding()}
                >
                  {t('fuel.rob.save')}
                </Button>
              </>
            ) : null}
            {sounding && adjustmentEditable ? (
              <>
                <Button
                  size="md"
                  leftSection={<IconFileText size={18} />}
                  loading={busy === 'request'}
                  disabled={busy !== null && busy !== 'request'}
                  onClick={() => void requestAdjustment()}
                >
                  {t('fuel.rob.request')}
                </Button>
                <Button
                  size="md"
                  leftSection={<IconSend size={18} />}
                  loading={busy === 'submit'}
                  disabled={busy !== null && busy !== 'submit'}
                  onClick={() => void submitAdjustment()}
                >
                  {t('fuel.rob.submit')}
                </Button>
              </>
            ) : null}
            {canDecide ? (
              <>
                <Button size="md" variant="default" onClick={() => setModal('return')}>
                  {pending?.office ? t('fuel.rob.returnOffice') : t('fuel.records.return')}
                </Button>
                <Button size="md" onClick={() => setModal('approve')}>
                  {pending?.office ? t('fuel.rob.approveOffice') : t('fuel.records.approve')}
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
        <DecisionModal
          mode={modal}
          recordNo={detail?.recordNo ?? ''}
          onClose={() => setModal(null)}
          onConfirm={(input) => (modal ? decide(modal, input) : Promise.resolve())}
        />
      </section>

      <PeriodPanel
        source={source}
        vesselId={vesselId}
        canClose={ship && data.can.close && source.soundingEditor !== undefined}
        tankName={tankName}
        fuelName={(id) => fuelCode(id)}
        closes={data.closes}
        onClosed={() => void load(soundingId)}
      />

      {settingsOpen && source.officeEditor ? (
        <ThresholdsModal
          source={source}
          thresholds={data.thresholds}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => {
            setSettingsOpen(false);
            setDone(t('fuel.rob.settingsSaved'));
            void load(soundingId);
          }}
        />
      ) : null}
    </div>
  );
}

/** Ofiste şirketin mutabakat eşikleri ve maliyet yöntemi (geçici ayar; ADR 0117 Ek Y4). */
function ThresholdsModal({
  source,
  thresholds,
  onClose,
  onSaved,
}: {
  source: FuelSource;
  thresholds: FuelThresholds;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useLedgerError();
  const [values, setValues] = useState<Record<string, { mt: string; pct: string }>>(() =>
    Object.fromEntries(
      METHODS.map((m) => [m, { mt: thresholds[m]?.mt ?? '', pct: thresholds[m]?.pct ?? '' }]),
    ),
  );
  const [costMethod, setCostMethod] = useState<string | null>(null);
  const [rowVersion, setRowVersion] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void source.settings?.().then((s) => {
      setCostMethod(s.costMethod);
      setRowVersion(s.rowVersion);
    });
  }, [source]);

  return (
    <Modal opened onClose={onClose} title={t('fuel.rob.thresholdsTitle')} centered size="lg">
      <p className={classes.hint} style={{ marginTop: 0 }}>
        {t('fuel.rob.thresholdsHint')}
      </p>
      <div className={cx.thresholdGrid}>
        {METHODS.map((m) => (
          <div key={m} className={cx.thresholdRow}>
            <TextInput
              label={t('fuel.rob.thresholdMt', { method: t(`fuel.rob.methods.${m}`) })}
              inputMode="decimal"
              value={values[m]?.mt ?? ''}
              onChange={(e) => {
                const v = e.currentTarget.value.replace(',', '.');
                setValues((p) => ({ ...p, [m]: { mt: v, pct: p[m]?.pct ?? '' } }));
              }}
            />
            <TextInput
              label={t('fuel.rob.thresholdPct', { method: t(`fuel.rob.methods.${m}`) })}
              inputMode="decimal"
              value={values[m]?.pct ?? ''}
              onChange={(e) => {
                const v = e.currentTarget.value.replace(',', '.');
                setValues((p) => ({ ...p, [m]: { mt: p[m]?.mt ?? '', pct: v } }));
              }}
            />
          </div>
        ))}
        <Select
          label={t('fuel.rob.costMethod')}
          data={(['weighted_average', 'batch'] as const).map((c) => ({
            value: c,
            label: t(`fuel.rob.costMethods.${c}`),
          }))}
          value={costMethod}
          clearable
          onChange={setCostMethod}
        />
      </div>
      {error ? (
        <Alert color="red" role="alert" mt="sm">
          {error}
        </Alert>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
        <Button variant="default" onClick={onClose} disabled={busy}>
          {t('fuel.rob.cancel')}
        </Button>
        <Button
          loading={busy}
          onClick={async () => {
            if (!source.officeEditor) return;
            setBusy(true);
            setError(null);
            try {
              const thresholds: Record<string, { mt: string | null; pct: string | null }> = {};
              for (const m of METHODS) {
                const v = values[m];
                const mtValue = v?.mt.trim() || null;
                const pctValue = v?.pct.trim() || null;
                if (mtValue !== null || pctValue !== null)
                  thresholds[m] = { mt: mtValue, pct: pctValue };
              }
              await source.officeEditor.saveSettings({
                rowVersion,
                values: { thresholds, costMethod },
              });
              onSaved();
            } catch (failure) {
              setError(errorText(failure));
            } finally {
              setBusy(false);
            }
          }}
        >
          {t('fuel.rob.saveSettings')}
        </Button>
      </div>
    </Modal>
  );
}
